import { Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { StatsigProvider } from '@statsig/react-bindings'
import { ScrollToTop } from './components/ScrollToTop'
import { ChatWidget } from './components/chat/ChatWidget'
import { ChatWidgetProvider } from './hooks/useChatWidget'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuth } from './hooks/useAuth'
import { statsigClient, setStatsigUser } from './lib/statsig'
import { lazyWithReload } from './lib/lazyWithReload'
import { Spinner } from './components/ui/Spinner'
// Home is the entry / LCP-critical page — keep it in the initial bundle.
import { Home } from './pages/Home'

// Everything else is code-split so it isn't shipped in the initial bundle.
const JobStatus = lazyWithReload(() => import('./pages/JobStatus').then(m => ({ default: m.JobStatus })))
const JobConfirmed = lazyWithReload(() => import('./pages/JobConfirmed').then(m => ({ default: m.JobConfirmed })))
const PlumberProfile = lazyWithReload(() => import('./pages/PlumberProfile').then(m => ({ default: m.PlumberProfile })))
const ClaimProfile = lazyWithReload(() => import('./pages/ClaimProfile').then(m => ({ default: m.ClaimProfile })))
const PlumberPortal = lazyWithReload(() => import('./pages/PlumberPortal').then(m => ({ default: m.PlumberPortal })))
const HowItWorks = lazyWithReload(() => import('./pages/HowItWorks').then(m => ({ default: m.HowItWorks })))
const LoginPage = lazyWithReload(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })))
const MyAccountDashboard = lazyWithReload(() => import('./pages/MyAccountDashboard').then(m => ({ default: m.MyAccountDashboard })))
const AuthCallback = lazyWithReload(() => import('./pages/AuthCallback').then(m => ({ default: m.AuthCallback })))
const PrivacyPolicy = lazyWithReload(() => import('./pages/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const TermsOfService = lazyWithReload(() => import('./pages/TermsOfService').then(m => ({ default: m.TermsOfService })))
const NotFound = lazyWithReload(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })))
const CompleteProfile = lazyWithReload(() => import('./pages/CompleteProfile').then(m => ({ default: m.CompleteProfile })))
const AdminLayout = lazyWithReload(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })))
const AdminAnalytics = lazyWithReload(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminAnalytics })))
const AdminJobs = lazyWithReload(() => import('./pages/admin/AdminJobs').then(m => ({ default: m.AdminJobs })))
const AdminJobDetail = lazyWithReload(() => import('./pages/admin/AdminJobDetail').then(m => ({ default: m.AdminJobDetail })))
const AdminPlumbers = lazyWithReload(() => import('./pages/admin/AdminPlumbers').then(m => ({ default: m.AdminPlumbers })))
const AdminSettings = lazyWithReload(() => import('./pages/admin/AdminSettings').then(m => ({ default: m.AdminSettings })))
const AdminNewCall = lazyWithReload(() => import('./pages/admin/AdminNewCall').then(m => ({ default: m.AdminNewCall })))
const AdminInboundContacts = lazyWithReload(() => import('./pages/admin/AdminInboundContacts').then(m => ({ default: m.AdminInboundContacts })))
const DirectoryRoutes = lazyWithReload(() => import('./pages/directory/DirectoryRoutes').then(m => ({ default: m.DirectoryRoutes })))
const DirectoryBrowse = lazyWithReload(() => import('./pages/directory/DirectoryBrowse').then(m => ({ default: m.DirectoryBrowse })))
const Review = lazyWithReload(() => import('./pages/Review').then(m => ({ default: m.Review })))
const BookingConfirmed = lazyWithReload(() => import('./pages/BookingConfirmed').then(m => ({ default: m.BookingConfirmed })))
const PurchaseComplete = lazyWithReload(() => import('./pages/PurchaseComplete').then(m => ({ default: m.PurchaseComplete })))
const HelpIndex = lazyWithReload(() => import('./pages/HelpIndex').then(m => ({ default: m.HelpIndex })))
const HelpArticle = lazyWithReload(() => import('./pages/HelpArticle').then(m => ({ default: m.HelpArticle })))
const BlogIndex = lazyWithReload(() => import('./pages/BlogIndex').then(m => ({ default: m.BlogIndex })))
const BlogPost = lazyWithReload(() => import('./pages/BlogPost').then(m => ({ default: m.BlogPost })))

function RedirectToMyAccount() {
  const { search } = useLocation()
  return <Navigate to={`/my-account${search}`} replace />
}

// Signed in → account dashboard; otherwise → email OTP login form.
function MyAccountGate() {
  const { user, loading } = useAuth()
  if (loading) return null
  return user ? <MyAccountDashboard /> : <LoginPage />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: 1,
    },
  },
})

// Attaches the signed-in customer's uid to Statsig events (anonymous visitors
// are tracked by Statsig's stableID). Renders nothing.
function StatsigUserSync() {
  const { user } = useAuth()
  useEffect(() => { setStatsigUser(user?.uid) }, [user?.uid])
  return null
}

function RouteFallback() {
  return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <Spinner size="lg" />
    </div>
  )
}

export function App() {
  // Warm the common route chunks once the page is idle, so in-app navigation to
  // the directory/blog/profile lands instantly instead of flashing the spinner.
  useEffect(() => {
    const preload = () => {
      void import('./pages/directory/DirectoryRoutes')
      void import('./pages/BlogIndex')
      void import('./pages/BlogPost')
      void import('./pages/PlumberProfile')
    }
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
    if (ric) ric(preload)
    else { const t = setTimeout(preload, 2000); return () => clearTimeout(t) }
    return undefined
  }, [])

  return (
    <StatsigProvider client={statsigClient}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <StatsigUserSync />
        <ScrollToTop />
        <ChatWidgetProvider>
        <ChatWidget />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            {/* Short link for the customer phone line's SMS auto-reply (issue
                #79) - a redirect rather than a second route rendering <Home />,
                so we never serve the homepage at two indexable URLs. The
                ?chat=1 param is what ChatWidgetProvider opens the panel on. */}
            <Route path="/chat" element={<Navigate to="/?chat=1" replace />} />
            <Route path="/job/:jobId" element={<JobStatus />} />
            <Route path="/job/:jobId/confirmed" element={<JobConfirmed />} />
            <Route path="/p/:id" element={<PlumberProfile />} />
            <Route path="/p/:id/claim" element={<ClaimProfile />} />
            <Route path="/portal" element={<PlumberPortal />} />
            <Route path="/my-account" element={<MyAccountGate />} />
            <Route path="/login" element={<RedirectToMyAccount />} />
            <Route path="/signup" element={<RedirectToMyAccount />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/help" element={<HelpIndex />} />
            <Route path="/help/:slug" element={<HelpArticle />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/category/:categorySlug" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Admin: persistent layout route — the sidebar stays mounted across
                page changes; lazy pages suspend only the content pane. */}
            <Route path="/admin" element={<Navigate to="/admin/analytics" replace />} />
            <Route element={<AdminLayout />}>
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/jobs" element={<AdminJobs />} />
              <Route path="/admin/jobs/:jobId" element={<AdminJobDetail />} />
              <Route path="/admin/plumbers" element={<AdminPlumbers />} />
              <Route path="/admin/calls/new" element={<AdminNewCall />} />
              <Route path="/admin/contacts" element={<AdminInboundContacts />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
            </Route>
            <Route path="/browse" element={<DirectoryBrowse />} />
            <Route path="/review" element={<Review />} />
            <Route path="/plumbers/*" element={<DirectoryRoutes />} />
            <Route path="/emergency-plumbers/*" element={<DirectoryRoutes />} />
            <Route path="/gas-safe-plumbers/*" element={<DirectoryRoutes />} />
            <Route path="/water-safe-plumbers/*" element={<DirectoryRoutes />} />
            {/* Direct booking retired 2026-08: old links/bookmarks land on the emergency form. */}
            <Route path="/book/:plumberId" element={<Navigate to="/" replace />} />
            <Route path="/booking/:bookingId/confirmed" element={<BookingConfirmed />} />
            <Route path="/purchase-complete" element={<PurchaseComplete />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </ChatWidgetProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </StatsigProvider>
  )
}
